// TOEFL iBT Mock 3 — Listening (2026 format, multistage adaptive)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Topics are unique to this mock (none are reused from Mocks 1, 4–7 or from
// Mock 3 Reading). The harder Module 2 uses indirect replies, inversion
// ("Had I known…", "Far be it from me…") and implied-meaning questions.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", M("Would you mind if I borrowed your notes from Monday's lecture?"), [
      "I borrowed a book on Monday.",
      "Go ahead, just bring them back by Friday.",
      "The lecture was in Room 12.",
      "I don't mind the lecture.",
    ], 1),
    response("m1-r2", W("The printer in the computer lab is out of toner again."), [
      "Let's use the one in the library, then.",
      "I printed my essay yesterday.",
      "The computer lab opens at eight.",
      "Toner is a kind of ink.",
    ], 0),
    response("m1-r3", M("Haven't you already signed up for the field trip?"), [
      "The trip is to the coast.",
      "I signed my name on the form.",
      "I meant to, but the list was already full.",
      "Field trips are usually fun.",
    ], 2),
    response("m1-r4", W("I can't decide between the statistics course and the research methods one."), [
      "Statistics was invented a long time ago.",
      "I decided last week.",
      "The course is full of research.",
      "Which one fits your schedule better?",
    ], 3),
    response("m1-r5", M("You'd better hurry if you want to catch the last shuttle."), [
      "Thanks, I'll grab my coat and go.",
      "I caught a cold last week.",
      "The shuttle is painted blue.",
      "I'm not hungry yet.",
    ], 0),
    response("m1-r6", W("Didn't Professor Grant say the quiz was cancelled?"), [
      "The quiz has ten questions.",
      "She said it's postponed, not cancelled.",
      "Professor Grant teaches biology.",
      "I didn't take the quiz.",
    ], 1),
    response("m1-r7", M("I wish I'd started my research paper earlier."), [
      "My research paper is about bees.",
      "Earlier is better than later.",
      "It's not too late to ask for an extension.",
      "I started yesterday morning.",
    ], 2),
    response("m1-r8", W("If the weather clears up, we could study outside."), [
      "I studied the weather report.",
      "The study room is inside.",
      "It cleared up my question.",
      "Good idea. It's too stuffy in here anyway.",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Writing Center Appointment",
      lines: [
        W("Hi, I'd like to book a session at the writing center. I have a history essay due next Thursday."),
        M("Sure. Is this your first visit? First-time students fill out a short form so the tutor knows what you're working on."),
        W("Yes, it's my first time. Can the tutor just proofread it for me? I mostly need someone to fix my grammar."),
        M("Actually, our tutors don't proofread. They'll help you organize your argument and point out patterns in your errors, so you can correct them yourself. It's more useful in the long run."),
        W("Oh, I see. Well, my professor did say my arguments were hard to follow last time."),
        M("Then that's a perfect thing to work on. Let's see... the earliest opening is Tuesday at two. Your deadline is Thursday, so that leaves you a day to revise."),
        W("Tuesday's a bit late for me. Is there anything sooner?"),
        M("Not in person, but we also run online sessions. There's one tomorrow evening at seven. Just make sure you upload your draft at least two hours before it starts."),
      ],
      questions: [
        {
          question: "Why does the woman go to the writing center?",
          options: [
            "To apply for a tutoring job",
            "To get help with an essay",
            "To complain about a tutor",
            "To hand in her history essay",
          ],
          correct: 1,
        },
        {
          question: "What does the man say about proofreading?",
          options: [
            "It costs extra.",
            "It is only available online.",
            "The tutors do not offer it.",
            "It takes about two hours.",
          ],
          correct: 2,
        },
        {
          question: "What will the woman most likely do next?",
          options: [
            "Book the Tuesday session",
            "Ask her professor for an extension",
            "Fill out the form next Thursday",
            "Take an online session tomorrow evening",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Campus Bike-Share Launch",
      lines: [
        M("Good morning, everyone. Starting next Monday, the university is launching a bike-share program. Fifty bikes will be available at four stations: the library, the science complex, the main dormitory and the sports center. Students can use a bike free of charge for up to two hours at a time; after that, a fee of one dollar per hour applies. To unlock a bike, you'll need to download the campus app and link it to your student ID. Helmets are not provided, so please bring your own. During the first week, volunteers will be at every station to show you how the system works."),
      ],
      questions: [
        {
          question: "What is the main purpose of the announcement?",
          options: [
            "To introduce a new bike-share program",
            "To announce new parking rules",
            "To advertise a cycling club",
            "To ask students to volunteer",
          ],
          correct: 0,
        },
        {
          question: "What must students do before they can use a bike?",
          options: [
            "Pay one dollar at the station",
            "Show their ID to a volunteer",
            "Pick up a helmet at the library",
            "Link the campus app to their student ID",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Art History: Colors in Medieval Manuscripts",
      lines: [
        W("Medieval manuscripts are famous for their brilliant colors, and many still look vivid after eight hundred years. So where did these colors come from? Some came from local plants and minerals. Red was often made from a mineral called cinnabar, and yellow from saffron or from a mineral containing arsenic, which, incidentally, was quite poisonous to the painters. But the most prized color of all was ultramarine, a deep blue made by grinding a stone called lapis lazuli. In the Middle Ages, almost all lapis lazuli came from a single group of mines in what is now Afghanistan, thousands of kilometers from the monasteries of Europe. That made ultramarine more expensive than gold, ounce for ounce. Contracts between artists and their patrons sometimes stated exactly how much ultramarine had to be used, and patrons often required that it be reserved for the most important figures in a picture, such as the robes of the Virgin Mary. So when you see a brilliant blue in a manuscript, you're seeing not just a color, but a statement about wealth and importance. Recently, scientists even found tiny particles of ultramarine in the teeth of a woman buried at a German monastery around the year one thousand. This suggests she was an illustrator who licked her brush to give it a fine point, and it tells us that women took part in producing these manuscripts, something historians had long underestimated."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How monasteries trained their painters",
            "Where medieval pigments came from and what they signified",
            "Why medieval manuscripts have faded over time",
            "The history of mining in Afghanistan",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, why was ultramarine so expensive?",
          options: [
            "It was mixed with real gold.",
            "Only monks were allowed to make it.",
            "It was difficult to grind into powder.",
            "Its raw material came from one distant region.",
          ],
          correct: 3,
        },
        {
          question: "Why does the professor mention the robes of the Virgin Mary?",
          options: [
            "To give an example of a figure that ultramarine was reserved for",
            "To show that religious paintings were rare",
            "To explain how blue cloth was dyed",
            "To describe a famous manuscript from Germany",
          ],
          correct: 0,
        },
        {
          question: "What does the discovery at the German monastery suggest?",
          options: [
            "Illustrators often became ill from their paints.",
            "Ultramarine was used as a medicine.",
            "Women helped to create medieval manuscripts.",
            "The monastery imported lapis lazuli directly.",
          ],
          correct: 2,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", W("I wouldn't count on the library being open over the holiday."), [
      "So I should check out the books I need now?",
      "I counted all the books yesterday.",
      "The holiday starts on a Friday.",
      "The library has a new front counter.",
    ], 0),
    response("up-r2", M("You're not the only one who found that exam tougher than expected."), [
      "I'm the only one in my family at college.",
      "The exam was held in the main hall.",
      "That's a relief, actually.",
      "I expected it to be on Tuesday.",
    ], 2),
    response("up-r3", W("Funny you should mention Professor Lang. I just ran into her at the café."), [
      "The café has funny names for its drinks.",
      "I ran there this morning.",
      "Mention it to the café staff.",
      "Did she say anything about the grant results?",
    ], 3),
    response("up-r4", M("Far be it from me to criticize, but your slides have rather a lot of text on them."), [
      "I'll trim them down before Friday.",
      "My slides are far from finished.",
      "I criticized the text last week.",
      "There are twenty slides in total.",
    ], 0),
    response("up-r5", W("Had I known the seminar was optional, I'd have stayed home."), [
      "I knew you'd stay home.",
      "Still, the discussion was pretty useful, wasn't it?",
      "Optional seminars are on Mondays.",
      "I'd have known that too.",
    ], 1),
    {
      id: "up-conv",
      type: "conversation",
      title: "Conference Travel Funding",
      lines: [
        M("Hi, Ms. Alvarez. A paper I worked on has been accepted at a conference in Montreal in April, and I was told the department might help with travel costs."),
        W("Congratulations! Yes, we have a travel fund for graduate students. It covers registration and up to six hundred dollars toward flights and accommodation."),
        M("That's great. The flight alone will be about five hundred, so that would help a lot."),
        W("There's one condition, though. The fund only supports students who are presenting, not just attending. You said the paper was accepted. Will you be presenting it yourself, or will your co-author?"),
        M("Well, my supervisor is the first author, and I think she was planning to present."),
        W("Then, strictly speaking, you wouldn't qualify. But if the two of you agree that you'll give the presentation, and she sends me an email confirming that, it's a different story."),
        M("I'm sure she'd be happy to. She's been encouraging me to present more."),
        W("Then get that sorted before the end of the month. The committee meets on the first, and anything that arrives after that waits for the next round, which would be too late for April."),
      ],
      questions: [
        {
          question: "What is the conversation mainly about?",
          options: [
            "Whether the student can get money to attend a conference",
            "How to book a cheaper flight to Montreal",
            "Why the student's paper was rejected",
            "How the student can change supervisors",
          ],
          correct: 0,
        },
        {
          question: "Why might the student not qualify for the fund at first?",
          options: [
            "His flight costs more than six hundred dollars.",
            "He has already received funding this year.",
            "His supervisor was expected to give the presentation.",
            "He has not yet registered for the conference.",
          ],
          correct: 2,
        },
        {
          question: "What does the woman imply when she says the next round \"would be too late for April\"?",
          options: [
            "The conference may be cancelled.",
            "He must apply before the committee's next meeting.",
            "The fund will run out of money in April.",
            "His supervisor must attend the committee meeting.",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Urban Planning: Why Wider Roads Don't End Traffic Jams",
      lines: [
        M("When a highway is congested, the intuitive solution is to add another lane. Yet transportation researchers have documented, again and again, that within a few years of a road being widened, traffic is often just as bad as before. This is called induced demand, and the logic is essentially economic. Driving has a cost, and a large part of that cost is time. When a road is congested, some people decide not to make certain trips, or they travel at a different hour, or they take the train. Widening the road lowers the time cost of driving, at least at first, so those people come back to the road. Some move to houses farther from their workplaces, because the commute now seems manageable. Businesses build along the highway. Eventually, the new capacity fills up. One large study of American cities found that the total distance driven rose almost exactly in proportion to the amount of new highway built, a ratio close to one to one. Now, this doesn't mean building roads is pointless. A wider road may carry many more people, and that has economic value. What it means is that widening rarely achieves the goal it's usually justified by, which is shorter commutes. Interestingly, the effect also works in reverse. When cities have removed or narrowed major roads, the traffic that planners feared would flood the surrounding streets has often partly disappeared, as people adjusted their habits."),
      ],
      questions: [
        {
          question: "What is the main point of the talk?",
          options: [
            "Highways should be replaced with train lines.",
            "Widening roads usually fails to reduce congestion in the long run.",
            "Most cities build too few roads for their populations.",
            "Traffic jams are caused mainly by accidents.",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, why do drivers return to a road after it is widened?",
          options: [
            "Fuel becomes cheaper.",
            "Public transportation becomes more expensive.",
            "The time cost of driving goes down.",
            "Businesses encourage them to drive.",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor mention a study of American cities?",
          options: [
            "To show that driving grew roughly in step with new highway construction",
            "To argue that American cities are unusually congested",
            "To compare American and European road design",
            "To show that most new roads are hardly used",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred about removing a major road?",
          options: [
            "It always causes severe congestion on nearby streets.",
            "It is too expensive for most cities to attempt.",
            "It increases the total distance people drive.",
            "Some of the traffic may vanish rather than simply move elsewhere.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", W("Where's the nearest bus stop?"), [
      "The bus is late.",
      "It's just around the corner.",
      "I take the bus every day.",
      "Yes, it stops.",
    ], 1),
    response("low-r2", M("Do you want to have lunch together?"), [
      "Sure, how about at noon?",
      "I had a sandwich.",
      "Lunch is a meal.",
      "The cafeteria is big.",
    ], 0),
    response("low-r3", W("When is the essay due?"), [
      "It's about history.",
      "It's two pages long.",
      "I wrote it at home.",
      "Next Monday, I think.",
    ], 3),
    response("low-r4", M("Is this seat taken?"), [
      "I took the train.",
      "It's a comfortable seat.",
      "No, go ahead and sit down.",
      "I have a seat at home.",
    ], 2),
    response("low-r5", W("How was your trip to the mountains?"), [
      "By car.",
      "Wonderful. The views were amazing.",
      "The mountains are tall.",
      "I'm going there next month.",
    ], 1),
    {
      id: "low-ann",
      type: "announcement",
      title: "Library Printing Changes",
      lines: [
        W("Attention, students. Starting next week, the library is changing how printing works. Instead of paying with coins, you will now pay with your student ID card. You can add money to your card at the machine next to the front desk. Black-and-white pages cost ten cents each, and color pages cost fifty cents. If you have any problems, please ask a staff member at the front desk."),
      ],
      questions: [
        {
          question: "What is changing at the library?",
          options: [
            "The opening hours",
            "The way students pay for printing",
            "The location of the front desk",
            "The price of student ID cards",
          ],
          correct: 1,
        },
        {
          question: "Where can students add money to their cards?",
          options: [
            "At a machine next to the front desk",
            "At the campus bookstore",
            "Only on the library website",
            "In the computer lab",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Zoology: How Camels Survive in the Desert",
      lines: [
        W("Camels are famous for living in hot, dry deserts. Many people believe that camels store water in their humps, but this is not true. A camel's hump is actually full of fat. When food is hard to find, the camel's body uses this fat for energy. So how do camels survive without water? First, they can drink a huge amount at once, more than one hundred liters in just a few minutes. Second, their bodies lose very little water. They only sweat when their body temperature gets very high. Camels also have long eyelashes and can close their noses, which protects them during sandstorms."),
      ],
      questions: [
        {
          question: "What is inside a camel's hump?",
          options: ["Water", "Fat", "Sand", "Muscle"],
          correct: 1,
        },
        {
          question: "How much water can a camel drink at one time?",
          options: [
            "About ten liters",
            "Less than one liter",
            "Exactly fifty liters",
            "More than one hundred liters",
          ],
          correct: 3,
        },
        {
          question: "How do camels protect themselves during sandstorms?",
          options: [
            "They hide in caves.",
            "They drink extra water.",
            "They close their noses and rely on long eyelashes.",
            "They sweat more than usual.",
          ],
          correct: 2,
        },
      ],
    },
  ],
};
